/**
 * Re-usable per-section card (#936 D4 / D5).
 *
 * Renders one Strategy Section row with:
 *   - Title + Markdown body (sanitized via DomSanitizer)
 *   - Policy badge (always)
 *   - Confidence pill (Foundations only — Plan sections show their bounds)
 *   - Autonomous-bounds metadata (Plan + Agent-Autonomous-Within-Bounds only)
 *   - Inline edit affordance (gated by `canEdit`)
 *   - "Diff history" button → emits `viewDiffsRequested`
 *
 * The component is a pure view: editing is delegated to the parent via the
 * `editRequested` output; the parent decides whether to inline-mount an
 * editor or open one elsewhere.
 */
import { Component, EventEmitter, Input, Output, SecurityContext, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import {
    confidenceVariant,
    parseAutonomousBounds,
    policyVariant,
    type StrategySectionRow,
} from './strategy-section-helpers';

@Component({
    standalone: false,
    selector: 'mkt-ws-strategy-section-card',
    template: `
<article class="ssc">
    <header class="ssc__head">
        <div class="ssc__head-left">
            <span class="ssc__caps">{{ section.SectionType }} · {{ section.SectionKey }}</span>
            <h3 class="ssc__title">{{ section.Title || section.SectionKey }}</h3>
        </div>
        <div class="ssc__head-actions">
            <mkt-ws-pill *ngIf="section.SectionType === 'Foundation' && section.ConfidenceLevel"
                         [variant]="confidenceVariant(section.ConfidenceLevel)"
                         [dot]="true">
                {{ section.ConfidenceLevel }}
            </mkt-ws-pill>
            <mkt-ws-pill [variant]="policyVariant(section.Policy)">{{ policyLabel(section.Policy) }}</mkt-ws-pill>
        </div>
    </header>

    <div class="ssc__body" [innerHTML]="renderedBody"></div>

    <div *ngIf="section.SectionType === 'Plan' && parsedBounds && Object.keys(parsedBounds).length > 0"
         class="ssc__bounds">
        <div class="ssc__caps">Autonomous bounds</div>
        <div class="ssc__bounds-grid">
            <div *ngFor="let entry of boundEntries" class="ssc__bound">
                <span class="ssc__bound-key">{{ entry.key }}</span>
                <span class="ssc__bound-value">{{ entry.value }}</span>
            </div>
        </div>
    </div>

    <div *ngIf="boundsParseError" class="ssc__bounds-error">
        <i class="fa-solid fa-triangle-exclamation"></i>
        Bounds JSON could not be parsed: {{ boundsParseError }}
    </div>

    <footer class="ssc__foot">
        <button *ngIf="canEdit" type="button" class="ssc__btn ssc__btn--primary" (click)="editRequested.emit(section)">
            <i class="fa-solid fa-pen"></i> Edit
        </button>
        <button type="button" class="ssc__btn" (click)="viewDiffsRequested.emit(section)">
            <i class="fa-solid fa-clock-rotate-left"></i> Diff history
        </button>
    </footer>
</article>
    `,
    styles: [`
        :host { display: block; }
        .ssc { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 18px 20px; margin-bottom: 16px; }
        .ssc__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 10px; flex-wrap: wrap; }
        .ssc__caps { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .ssc__title { font-size: 17px; font-weight: 600; line-height: 1.3; color: var(--mj-text-primary, #1a1a1a); margin: 4px 0 0; }
        .ssc__head-actions { display: flex; align-items: center; gap: 8px; }

        .ssc__body { font-size: 14px; line-height: 1.65; color: var(--mj-text-secondary, #3f3f46); }
        .ssc__body :first-child { margin-top: 0; }
        .ssc__body p { margin: 8px 0; }
        .ssc__body h3, .ssc__body h4 { color: var(--mj-text-primary, #1a1a1a); margin: 16px 0 6px; font-weight: 600; }
        .ssc__body ul, .ssc__body ol { padding-left: 22px; margin: 8px 0; }
        .ssc__body li { margin: 4px 0; }
        .ssc__body code { background: var(--mj-bg-page, #f5f5f5); padding: 1px 5px; border-radius: 3px; font-size: 12.5px; }

        .ssc__bounds { margin-top: 12px; padding: 10px 12px; background: var(--mj-bg-page, #f5f5f5); border-radius: 8px; }
        .ssc__bounds-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 6px 12px; margin-top: 6px; }
        .ssc__bound { font-size: 12px; }
        .ssc__bound-key { color: var(--mj-text-secondary, #71717a); margin-right: 4px; }
        .ssc__bound-value { color: var(--mj-text-primary, #1a1a1a); font-family: ui-monospace, monospace; }
        .ssc__bounds-error { margin-top: 12px; padding: 8px 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: #b91c1c; font-size: 12.5px; }

        .ssc__foot { display: flex; gap: 8px; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--mj-border-default, #e0e0e0); }
        .ssc__btn { padding: 5px 12px; border-radius: 6px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); cursor: pointer; font-size: 12.5px; color: var(--mj-text-primary, #1a1a1a); }
        .ssc__btn:hover { background: var(--mj-bg-page, #f5f5f5); }
        .ssc__btn--primary { background: var(--mj-text-primary, #1a1a1a); color: var(--mj-bg-surface, #fff); border-color: var(--mj-text-primary, #1a1a1a); }
        .ssc__btn--primary:hover { background: #000; }
    `]
})
export class StrategySectionCardComponent {
    @Input({ required: true }) section!: StrategySectionRow;
    @Input() canEdit = false;

    @Output() editRequested = new EventEmitter<StrategySectionRow>();
    @Output() viewDiffsRequested = new EventEmitter<StrategySectionRow>();

    private sanitizer = inject(DomSanitizer);

    // exposed to template for `Object.keys(parsedBounds)` check
    readonly Object = Object;

    get renderedBody(): SafeHtml {
        const body = this.section?.Body ?? '';
        const html = marked.parse(body, { async: false }) as string;
        return this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    }

    get parsedBounds(): Record<string, unknown> | null {
        return parseAutonomousBounds(this.section).bounds;
    }

    get boundsParseError(): string | null {
        return parseAutonomousBounds(this.section).parseError;
    }

    get boundEntries(): { key: string; value: string }[] {
        const b = this.parsedBounds;
        if (!b) return [];
        return Object.keys(b).map(k => ({
            key: k,
            value: typeof b[k] === 'string' || typeof b[k] === 'number' || typeof b[k] === 'boolean'
                ? String(b[k])
                : JSON.stringify(b[k]),
        }));
    }

    confidenceVariant(level: StrategySectionRow['ConfidenceLevel']) {
        return confidenceVariant(level);
    }
    policyVariant(policy: StrategySectionRow['Policy']) {
        return policyVariant(policy);
    }
    policyLabel(policy: StrategySectionRow['Policy']): string {
        switch (policy) {
            case 'Leadership-Set': return 'Leadership-set';
            case 'Founder-Approved': return 'Founder approved';
            case 'Agent-Proposed-Founder-Approved': return 'Agent-proposed';
            case 'Agent-Autonomous-Within-Bounds': return 'Autonomous';
            default: return policy;
        }
    }
}
